<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { debounce } from '@chatwoot/utils';
import { useSuppliersStore } from 'dashboard/stores/suppliers';
import SupplierAPI from 'dashboard/api/suppliers';

import SuppliersListLayout from 'dashboard/components-next/Suppliers/SuppliersListLayout.vue';
import SuppliersCard from 'dashboard/components-next/Suppliers/SuppliersCard.vue';
import SupplierFormModal from 'dashboard/components-next/Suppliers/SupplierFormModal.vue';

const DEBOUNCE_DELAY = 300;

const suppliersStore = useSuppliersStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const suppliers = computed(() => suppliersStore.getSuppliersList);
const meta = computed(() => suppliersStore.getMeta);
const uiFlags = computed(() => suppliersStore.getUIFlags);

const searchQuery = computed(() => route.query?.search || '');
const searchValue = ref(searchQuery.value);
const pageNumber = computed(() => Number(route.query?.page) || 1);

const supplierFormModalRef = ref(null);
const isCreatingOrUpdating = ref(false);
const editingSupplier = ref(null);
const isFetchingList = computed(() => uiFlags.value.fetchingList);

const updateURLParams = (page, search = '') => {
  const query = {
    ...route.query,
    page: page.toString(),
  };

  if (search) {
    query.search = search;
  } else {
    delete query.search;
  }

  router.replace({ query });
};

const fetchSuppliers = async (page, search) => {
  const currentPage = page ?? pageNumber.value;
  const currentSearch = search ?? searchQuery.value;

  if (page !== undefined || search !== undefined) {
    updateURLParams(currentPage, currentSearch);
  }

  if (currentSearch) {
    await suppliersStore.search({
      search: currentSearch,
      page: currentPage,
      sort: 'name',
    });
  } else {
    await suppliersStore.get({
      page: currentPage,
      sort: 'name',
    });
  }
};

const onSearch = debounce(query => {
  searchValue.value = query;
  fetchSuppliers(1, query);
}, DEBOUNCE_DELAY);

const onPageChange = page => {
  fetchSuppliers(page, searchValue.value);
};

const openCreateModal = () => {
  editingSupplier.value = null;
  supplierFormModalRef.value?.open();
};

const syncUsers = async (supplierId, userIds) => {
  const { data } = await SupplierAPI.getUsers(supplierId);
  const currentIds = (data.payload || []).map(user => user.id);
  const desiredIds = (userIds || []).map(Number);
  const toAdd = desiredIds.filter(id => !currentIds.includes(id));
  const toRemove = currentIds.filter(id => !desiredIds.includes(id));
  await Promise.all([
    ...toAdd.map(id => SupplierAPI.addUser(supplierId, id)),
    ...toRemove.map(id => SupplierAPI.removeUser(supplierId, id)),
  ]);
};

const handleSaveSupplier = async payload => {
  isCreatingOrUpdating.value = true;
  try {
    const { user_ids: userIds, ...supplierPayload } = payload;
    let supplierId = editingSupplier.value?.id;
    if (editingSupplier.value) {
      await suppliersStore.update({
        ...supplierPayload,
        id: editingSupplier.value.id,
      });
      useAlert(t('SUPPLIERS.FORM.UPDATE_SUCCESS'));
    } else {
      const created = await suppliersStore.create(supplierPayload);
      supplierId = created.id;
      useAlert(t('SUPPLIERS.FORM.CREATE_SUCCESS'));
    }
    if (supplierId) await syncUsers(supplierId, userIds);
    supplierFormModalRef.value?.close();
    await fetchSuppliers(pageNumber.value, searchValue.value);
  } catch (error) {
    const message =
      error.message ||
      (editingSupplier.value
        ? t('SUPPLIERS.FORM.UPDATE_ERROR')
        : t('SUPPLIERS.FORM.CREATE_ERROR'));
    useAlert(message);
  } finally {
    isCreatingOrUpdating.value = false;
  }
};

const handleSaveSupplierWrapper = payload => {
  handleSaveSupplier(payload).catch(err => {
    console.error('Unhandled error in save handler:', err);
  });
};

onMounted(() => {
  searchValue.value = searchQuery.value;
  fetchSuppliers();
});
</script>

<template>
  <SuppliersListLayout
    :search-value="searchValue"
    :header-title="t('SUPPLIERS.HEADER')"
    :current-page="pageNumber"
    :total-items="Number(meta.totalCount || 0)"
    :show-pagination-footer="!!suppliers.length"
    @update:current-page="onPageChange"
    @search="onSearch"
    @create="openCreateModal"
  >
    <div v-if="isFetchingList" class="flex items-center justify-center p-8">
      <span class="text-n-slate-11 text-base">{{
        t('SUPPLIERS.LOADING')
      }}</span>
    </div>
    <div
      v-else-if="suppliers.length === 0"
      class="flex items-center justify-center p-8"
    >
      <span class="text-n-slate-11 text-base">{{
        t('SUPPLIERS.EMPTY_STATE.TITLE')
      }}</span>
    </div>
    <div v-else class="flex flex-col gap-4 p-4">
      <SuppliersCard
        v-for="supplier in suppliers"
        :id="supplier.id"
        :key="supplier.id"
        :name="supplier.name"
        :owner-name="supplier.owner?.name"
        :contact-name="supplier.contact?.name"
        :users-count="(supplier.users || []).length"
        :updated-at="supplier.updatedAt"
      />
    </div>
  </SuppliersListLayout>

  <SupplierFormModal
    ref="supplierFormModalRef"
    :supplier="editingSupplier"
    :is-loading="isCreatingOrUpdating"
    @save="handleSaveSupplierWrapper"
  />
</template>
